import { z } from "zod";

/**
 * StepFingerprint — per-step structural signal computed by the runner.
 * Shape is single-sourced here; both runners, the API ingress, and any
 * downstream consumer (skip cascade, visual diff) import from this file.
 *
 * See openspec/changes/archive/2026-04-22-step-fingerprint-capture/design.md (D2, D3).
 *
 * Empty-string hashes ("") represent a per-field soft-fail — the runner
 * encountered an error computing that specific signal but the step still
 * succeeded. Consumers MUST treat "" as "unknown", never as a match value.
 * A "" hash will never match another "" hash for skip-cascade purposes.
 */

export const stepActionTypeEnum = z.enum([
  "click",
  "fill",
  "hover",
  "navigate",
  "scroll",
  "press",
  "done",
  "note",
]);

export const stepActionAnchorSchema = z.object({
  /** Max 512 chars to prevent bloating JSONB storage. */
  role: z.string().max(512).optional(),
  /** Max 512 chars to prevent bloating JSONB storage. */
  accessibleName: z.string().max(512).optional(),
  /** Max 512 chars to prevent bloating JSONB storage. */
  text: z.string().max(512).optional(),
});

export const stepActionSchema = z.object({
  type: stepActionTypeEnum,
  anchor: stepActionAnchorSchema.optional(),
  /** Max 10 fallback selectors, each capped at 1024 chars. */
  selectorFallbacks: z.array(z.string().max(1024)).max(10).optional(),
});

export const networkFingerprintEntrySchema = z.object({
  /** HTTP method, max 16 chars (e.g. "GET", "POST", "OPTIONS"). */
  method: z.string().max(16),
  /** Templated URL pattern, max 2048 chars. */
  urlPattern: z.string().max(2048),
  status: z.number().int().nonnegative(),
});

// Hash length invariants (see D2):
//   domHash / axTreeHash — sha256 hex → 64 chars (or "" on per-field soft-fail).
//   screenshotPHash     — 64-bit pHash hex → 16 chars (or "").
const hex = (len: number) =>
  z
    .string()
    .regex(
      new RegExp(`^([0-9a-f]{${len}})?$`),
      `expected hex string of length ${len} or ""`,
    );

export const stepFingerprintSchema = z.object({
  /** Full page URL at time of step, max 4096 chars. */
  url: z.string().max(4096),
  action: stepActionSchema,
  domHash: hex(64),
  axTreeHash: hex(64),
  screenshotPHash: hex(16),
  /** Max 500 network entries to prevent runner abuse bloating JSONB. */
  networkFingerprint: z.array(networkFingerprintEntrySchema).max(500),
  consoleErrorCount: z.number().int().nonnegative(),
  jsExceptionCount: z.number().int().nonnegative(),
  settleDurationMs: z.number().int().nonnegative(),
});

export type StepActionType = z.infer<typeof stepActionTypeEnum>;
export type StepActionAnchor = z.infer<typeof stepActionAnchorSchema>;
export type StepAction = z.infer<typeof stepActionSchema>;
export type NetworkFingerprintEntry = z.infer<
  typeof networkFingerprintEntrySchema
>;
export type StepFingerprint = z.infer<typeof stepFingerprintSchema>;
